// src/pages/LeaderStats.js
import React, { useCallback, useEffect, useMemo, useState } from "react";
import PageTransition from "../components/PageTransition";
import axios from "axios";
import toast from "react-hot-toast";
import { BASE_ENDPOINT } from "../helpers/Config";
import {
    FaArrowLeft,
    FaSyncAlt,
    FaUserCircle,
    FaPhoneAlt,
    FaEnvelope,
    FaCheckCircle,
    FaTimesCircle,
    FaCalendarAlt,
    FaChartBar,
    FaCoins,
    FaWallet,
    FaMoneyBillWave,
    FaExchangeAlt,
    FaUsers,
    FaLayerGroup,
    FaFilePdf,
} from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

/**
 * API:
 *  POST `${BASE_ENDPOINT}/admin/leader-stats`
 *  payload: { token, username, from_date, to_date } (YYYY-MM-DD)
 */
const LEADER_STATS_URL = `${BASE_ENDPOINT}/admin/leader-stats`;

/* ----------------- Utils ----------------- */
function safeText(v) {
    return v === null || v === undefined || v === "" ? "—" : String(v);
}

function nfmt2(v) {
    const num = Number(v);
    if (!Number.isFinite(num)) return "0.00";
    return new Intl.NumberFormat("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(num);
}

function cfmt(v) {
    const num = Number(v);
    if (!Number.isFinite(num)) return "0";
    return new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(num);
}

function toYMD(d = new Date()) {
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/* ----------------- UI Components ----------------- */
function StatPill({ icon, label, value }) {
    return (
        <div className="stat-pill">
            <div className="stat-pill-icon">{icon}</div>
            <div className="stat-pill-meta">
                <div className="stat-pill-label">{label}</div>
                <div className="stat-pill-value">{value}</div>
            </div>
        </div>
    );
}

function MiniCard({ title, icon, children, tone = "blue" }) {
    return (
        <div className={`mini-card tone-${tone}`}>
            <div className="mini-card-head">
                <div className="mini-card-title">
                    <span className="mini-card-ic">{icon}</span>
                    <span>{title}</span>
                </div>
            </div>
            <div className="mini-card-body">{children}</div>
        </div>
    );
}

function KeyValRow({ k, v, right = false }) {
    return (
        <div className="kv-row">
            <div className="kv-k">{k}</div>
            <div className={`kv-v ${right ? "text-end" : ""}`}>{v}</div>
        </div>
    );
}

function LevelSection({ level, deposit, withdrawal, trade, balance, commission, defaultOpen = true }) {
    const dep = Number(deposit || 0);
    const wd = Number(withdrawal || 0);

    const t = trade || {};
    const b = balance || {};
    const c = commission || {};

    const coin = Number(b.coin || 0);
    const lock = Number(b.lock || 0);
    const usdt = Number(b.usdt || 0);

    const comTotal =
        Number(c.refer || 0) + Number(c.self_deposit || 0) + Number(c.deposit || 0) + Number(c.trade || 0);

    return (
        <details className="level-wrap" open={defaultOpen}>
            <summary className="level-summary">
                <div className="level-summary-left">
                    <div className="level-badge">
                        <FaLayerGroup /> Level {level}
                    </div>
                    <div className="level-sub">
                        Deposit <span className="fw-semibold">{nfmt2(dep)}</span> • Withdrawal{" "}
                        <span className="fw-semibold">{nfmt2(wd)}</span> • Net{" "}
                        <span className="fw-semibold">{nfmt2(dep - wd)}</span>
                    </div>
                </div>

                <div className="level-summary-right">
                    <span className="pill pill-blue">
                        <FaCoins className="me-2" />
                        Commission: {nfmt2(comTotal)}
                    </span>
                    <span className="pill pill-slate">
                        <FaWallet className="me-2" />
                        Coin: {nfmt2(coin)}
                    </span>
                </div>
            </summary>

            <div className="level-body">
                <div className="row g-3">
                    <div className="col-12 col-lg-4">
                        <MiniCard title="Money Flow" icon={<FaMoneyBillWave />} tone="green">
                            <KeyValRow k="Deposit" v={nfmt2(dep)} right />
                            <KeyValRow k="Withdrawal" v={nfmt2(wd)} right />
                            <div className="kv-divider" />
                            <KeyValRow k="Net Flow (D - W)" v={nfmt2(dep - wd)} right />
                        </MiniCard>
                    </div>

                    <div className="col-12 col-lg-4">
                        <MiniCard title="Balance Snapshot" icon={<FaWallet />} tone="blue">
                            <KeyValRow k="Coin" v={nfmt2(coin)} right />
                            <KeyValRow k="Locked" v={nfmt2(lock)} right />
                            <div className="kv-divider" />
                            <KeyValRow k="USDT" v={nfmt2(usdt)} right />
                        </MiniCard>
                    </div>

                    <div className="col-12 col-lg-4">
                        <MiniCard title="Commission" icon={<FaCoins />} tone="purple">
                            <KeyValRow k="Refer" v={nfmt2(c.refer || 0)} right />
                            <KeyValRow k="Self Deposit" v={nfmt2(c.self_deposit || 0)} right />
                            <KeyValRow k="Deposit" v={nfmt2(c.deposit || 0)} right />
                            <KeyValRow k="Trade" v={nfmt2(c.trade || 0)} right />
                            <div className="kv-divider" />
                            <KeyValRow k="Total" v={nfmt2(comTotal)} right />
                        </MiniCard>
                    </div>

                    <div className="col-12">
                        <MiniCard title="Trade" icon={<FaChartBar />} tone="amber">
                            <div className="trade-grid">
                                <div className="trade-col">
                                    <div className="trade-h">Counts</div>
                                    <KeyValRow k="Pending" v={cfmt(t.pending_count || 0)} right />
                                    <KeyValRow k="Win" v={cfmt(t.win_count || 0)} right />
                                    <KeyValRow k="Loss" v={cfmt(t.loss_count || 0)} right />
                                </div>

                                <div className="trade-col">
                                    <div className="trade-h">Amounts</div>
                                    <KeyValRow k="Total Amount" v={nfmt2(t.total_amount || 0)} right />
                                    <KeyValRow k="Pending Amount" v={nfmt2(t.pending_amount || 0)} right />
                                    <KeyValRow k="Win Amount" v={nfmt2(t.win_amount || 0)} right />
                                    <KeyValRow k="Loss Amount" v={nfmt2(t.loss_amount || 0)} right />
                                </div>
                            </div>
                        </MiniCard>
                    </div>
                </div>
            </div>
        </details>
    );
}

/* ----------------- Main Page ----------------- */
export default function LeaderStats() {
    const { username } = useParams();
    const navigate = useNavigate();
    const { logout } = useAuth();

    const today = useMemo(() => toYMD(new Date()), []);
    const [fromDate, setFromDate] = useState(today);
    const [toDate, setToDate] = useState(today);

    const [loading, setLoading] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [lastUpdated, setLastUpdated] = useState(null);

    const emptyReport = useMemo(
        () => ({
            profile: null,
            stats: {
                deposit: {},
                withdrawal: {},
                trade: {},
                balance: {},
                commission: {},
                self: { deposit: 0, withdrawal: 0 }
            },
            salary_record: [],
        }),
        []
    );

    const [report, setReport] = useState(emptyReport);

    const validateDates = useCallback(() => {
        if (!fromDate || !toDate) return "Please select both From Date and To Date";
        if (!/^\d{4}-\d{2}-\d{2}$/.test(fromDate)) return "From Date must be in YYYY-MM-DD format";
        if (!/^\d{4}-\d{2}-\d{2}$/.test(toDate)) return "To Date must be in YYYY-MM-DD format";
        if (fromDate > toDate) return "From Date cannot be greater than To Date";
        return null;
    }, [fromDate, toDate]);

    const clearLoadedData = useCallback(() => {
        setReport(emptyReport);
        setLastUpdated(null);
    }, [emptyReport]);

    const fetchReport = useCallback(async () => {
        const token = localStorage.getItem("token") || "";
        if (!token) {
            toast.error("Token not found. Please login again.");
            logout();
            return;
        }

        const err = validateDates();
        if (err) {
            toast.error(err);
            return;
        }

        setLoading(true);
        try {
            const res = await axios({
                method: "post",
                url: LEADER_STATS_URL,
                headers: { "Content-Type": "application/json" },
                data: { token, username, from_date: fromDate, to_date: toDate },
            });

            const out = res?.data;

            if (out?.error) {
                if (out?.error === "session expired") {
                    logout();
                    return;
                }
                toast.error(typeof out?.error === "string" ? out.error : "Failed to load report");
                clearLoadedData();
                return;
            }

            const data = out?.data || {};
            setReport({
                profile: data?.profile || null,
                stats: {
                    deposit: data?.stats?.deposit || {},
                    withdrawal: data?.stats?.withdrawal || {},
                    trade: data?.stats?.trade || {},
                    balance: data?.stats?.balance || {},
                    commission: data?.stats?.commission || {},
                    self: data?.stats?.self || { deposit: 0, withdrawal: 0 }
                },
                salary_record: Array.isArray(data?.salary_record) ? data.salary_record : [],
            });

            setLastUpdated(new Date());
        } catch (e) {
            console.log(e);
            toast.error("Failed to load report");
        } finally {
            setLoading(false);
        }
    }, [fromDate, toDate, username, logout, validateDates, clearLoadedData]);

    // On page open: fetch once for today (and keep it loaded)
    useEffect(() => {
        fetchReport();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const profile = report?.profile;

    const mobileDisplay = useMemo(() => {
        const mobile = safeText(profile?.mobile);
        const cc = safeText(profile?.country_code);
        if (mobile === "—") return "—";
        return `+${cc === "—" ? "" : cc} ${mobile}`.trim();
    }, [profile]);

    const lastUpdatedText = useMemo(() => {
        if (!lastUpdated) return "—";
        return lastUpdated.toLocaleString("en-IN");
    }, [lastUpdated]);

    // Levels union from all stats (deposit/withdrawal/trade/balance + commission typo leve_)
    const levels = useMemo(() => {
        const s = report?.stats || {};
        const set = new Set();

        const addLevelKeys = (obj, rx) => {
            if (!obj || typeof obj !== "object") return;
            Object.keys(obj).forEach((k) => {
                const m = k.match(rx);
                if (m && m[1]) set.add(Number(m[1]));
            });
        };

        addLevelKeys(s.deposit, /^level_(\d+)$/i);
        addLevelKeys(s.withdrawal, /^level_(\d+)$/i);
        addLevelKeys(s.trade, /^level_(\d+)$/i);
        addLevelKeys(s.balance, /^level_(\d+)$/i);
        addLevelKeys(s.commission, /^leve_(\d+)$/i);

        const arr = Array.from(set).filter((n) => Number.isFinite(n)).sort((a, b) => a - b);
        return arr.length ? arr : [1, 2, 3, 4, 5, 6];
    }, [report]);

    // Totals (sum of all levels)
    const totals = useMemo(() => {
        const s = report?.stats || {};
        const dep = s.deposit || {};
        const wd = s.withdrawal || {};
        const trade = s.trade || {};
        const bal = s.balance || {};
        const com = s.commission || {};

        let depositTotal = 0;
        let withdrawalTotal = 0;

        const balanceTotals = { coin: 0, lock: 0, usdt: 0 };

        const commissionTotals = { refer: 0, self_deposit: 0, deposit: 0, trade: 0, total: 0 };

        const tradeTotals = {
            total_amount: 0,
            pending_count: 0,
            win_count: 0,
            loss_count: 0,
            pending_amount: 0,
            win_amount: 0,
            loss_amount: 0,
        };

        levels.forEach((n) => {
            depositTotal += Number(dep[`level_${n}`] || 0);
            withdrawalTotal += Number(wd[`level_${n}`] || 0);

            const b = bal[`level_${n}`] || {};
            balanceTotals.coin += Number(b.coin || 0);
            balanceTotals.lock += Number(b.lock || 0);
            balanceTotals.usdt += Number(b.usdt || 0);

            const c = com[`leve_${n}`] || {};
            const refer = Number(c.refer || 0);
            const selfDep = Number(c.self_deposit || 0);
            const depCom = Number(c.deposit || 0);
            const trCom = Number(c.trade || 0);
            commissionTotals.refer += refer;
            commissionTotals.self_deposit += selfDep;
            commissionTotals.deposit += depCom;
            commissionTotals.trade += trCom;
            commissionTotals.total += refer + selfDep + depCom + trCom;

            const t = trade[`level_${n}`] || {};
            tradeTotals.total_amount += Number(t.total_amount || 0);
            tradeTotals.pending_count += Number(t.pending_count || 0);
            tradeTotals.win_count += Number(t.win_count || 0);
            tradeTotals.loss_count += Number(t.loss_count || 0);
            tradeTotals.pending_amount += Number(t.pending_amount || 0);
            tradeTotals.win_amount += Number(t.win_amount || 0);
            tradeTotals.loss_amount += Number(t.loss_amount || 0);
        });

        return {
            depositTotal,
            withdrawalTotal,
            netFlow: depositTotal - withdrawalTotal,
            balanceTotals,
            commissionTotals,
            tradeTotals,
        };
    }, [report, levels]);

    // Get self deposit and withdrawal values
    const selfDeposit = useMemo(() => Number(report?.stats?.self?.deposit || 0), [report]);
    const selfWithdrawal = useMemo(() => Number(report?.stats?.self?.withdrawal || 0), [report]);
    const selfNetFlow = useMemo(() => selfDeposit - selfWithdrawal, [selfDeposit, selfWithdrawal]);

    const onSubmit = (e) => {
        e.preventDefault();
        fetchReport();
    };

    /* ----------------- PDF (client-side generation) ----------------- */
    const downloadPdf = useCallback(async () => {
        const err = validateDates();
        if (err) {
            toast.error(err);
            return;
        }

        if (!report?.profile) {
            toast.error("Report not loaded.");
            return;
        }

        setDownloading(true);
        try {
            const doc = new jsPDF({ unit: "pt", format: "a4" });
            const pageWidth = doc.internal.pageSize.getWidth();
            const marginX = 40;

            const addTitle = (text, y) => {
                doc.setFont("helvetica", "bold");
                doc.setFontSize(16);
                doc.text(text, marginX, y);
                return y + 18;
            };

            const addLine = (text, y) => {
                doc.setFont("helvetica", "normal");
                doc.setFontSize(10);
                doc.setTextColor(60);
                doc.text(text, marginX, y);
                doc.setTextColor(0);
                return y + 14;
            };

            const addSectionHeader = (text, y) => {
                doc.setFont("helvetica", "bold");
                doc.setFontSize(12);
                doc.text(text, marginX, y);
                doc.setDrawColor(220);
                doc.line(marginX, y + 6, pageWidth - marginX, y + 6);
                return y + 18;
            };

            const addKeyValTable = (rows, startY) => {
                autoTable(doc, {
                    startY,
                    theme: "grid",
                    head: [["Item", "Value"]],
                    body: rows,
                    styles: { fontSize: 9, cellPadding: 6 },
                    headStyles: { fillColor: [13, 110, 253], textColor: 255 },
                    columnStyles: { 1: { halign: "right" } },
                    margin: { left: marginX, right: marginX },
                });
                return doc.lastAutoTable.finalY + 12;
            };

            let y = 48;

            // Title changed
            y = addTitle("Team Report", y);

            // After title: name, mobile, email
            y = addLine(`Name: ${safeText(report?.profile?.name)}`, y);
            y = addLine(`Mobile: ${mobileDisplay}`, y);
            y = addLine(`Email: ${safeText(report?.profile?.email)}`, y);

            // Remove "Generated" text; keep only Period
            y = addLine(`Period: ${fromDate} to ${toDate}`, y);
            y += 6;

            // Self Transaction Summary (moved above Current Balances)
            y = addSectionHeader("Self Transaction Summary", y);
            y = addKeyValTable(
                [
                    ["Self Deposit", nfmt2(selfDeposit)],
                    ["Self Withdrawal", nfmt2(selfWithdrawal)],
                    ["Self Net Flow", nfmt2(selfNetFlow)],
                ],
                y
            );

            // Current Balances (not date range dependent)
            y = addSectionHeader("Current Balances (All Levels)", y);
            y = addKeyValTable(
                [
                    ["Total Coin", nfmt2(totals.balanceTotals.coin)],
                    ["Total Locked Coin", nfmt2(totals.balanceTotals.lock)],
                    ["Total USDT", nfmt2(totals.balanceTotals.usdt)],
                ],
                y
            );

            // Team Transaction Summary (date range dependent)
            y = addSectionHeader("Team Transaction Summary", y);
            y = addKeyValTable(
                [
                    ["Total Deposit", nfmt2(totals.depositTotal)],
                    ["Total Withdrawal", nfmt2(totals.withdrawalTotal)],
                    ["Net Flow (D - W)", nfmt2(totals.netFlow)],
                    ["Trade Total Amount", nfmt2(totals.tradeTotals.total_amount)],
                    [
                        "Trade Pending / Win / Loss",
                        `${cfmt(totals.tradeTotals.pending_count)} / ${cfmt(totals.tradeTotals.win_count)} / ${cfmt(
                            totals.tradeTotals.loss_count
                        )}`,
                    ],
                ],
                y
            );

            // Commission totals block
            y = addSectionHeader("Commission Totals", y);
            y = addKeyValTable(
                [
                    ["Refer", nfmt2(totals.commissionTotals.refer)],
                    ["Self Deposit", nfmt2(totals.commissionTotals.self_deposit)],
                    ["Deposit", nfmt2(totals.commissionTotals.deposit)],
                    ["Trade", nfmt2(totals.commissionTotals.trade)],
                    ["Total", nfmt2(totals.commissionTotals.total)],
                ],
                y
            );

            // Level-wise blocks
            y = addSectionHeader("Level-wise Breakdown", y);

            levels.forEach((lvl) => {
                const s = report?.stats || {};
                const dep = Number(s?.deposit?.[`level_${lvl}`] ?? 0);
                const wd = Number(s?.withdrawal?.[`level_${lvl}`] ?? 0);
                const trade = s?.trade?.[`level_${lvl}`] || {};
                const balance = s?.balance?.[`level_${lvl}`] || {};
                const commission = s?.commission?.[`leve_${lvl}`] || {}; // API typo

                const coin = Number(balance.coin || 0);
                const lock = Number(balance.lock || 0);
                const usdt = Number(balance.usdt || 0);

                const comTotal =
                    Number(commission.refer || 0) +
                    Number(commission.self_deposit || 0) +
                    Number(commission.deposit || 0) +
                    Number(commission.trade || 0);

                doc.setFont("helvetica", "bold");
                doc.setFontSize(11);
                doc.text(`Level ${lvl}`, marginX, y);
                y += 8;

                autoTable(doc, {
                    startY: y,
                    theme: "grid",
                    head: [["Category", "Item", "Value"]],
                    body: [
                        ["Money Flow", "Deposit", nfmt2(dep)],
                        ["Money Flow", "Withdrawal", nfmt2(wd)],
                        ["Money Flow", "Net Flow (D - W)", nfmt2(dep - wd)],

                        ["Balance", "Coin", nfmt2(coin)],
                        ["Balance", "Locked", nfmt2(lock)],
                        ["Balance", "USDT", nfmt2(usdt)],

                        ["Commission", "Refer", nfmt2(commission.refer || 0)],
                        ["Commission", "Self Deposit", nfmt2(commission.self_deposit || 0)],
                        ["Commission", "Deposit", nfmt2(commission.deposit || 0)],
                        ["Commission", "Trade", nfmt2(commission.trade || 0)],
                        ["Commission", "Total", nfmt2(comTotal)],

                        ["Trade (Counts)", "Pending", cfmt(trade.pending_count || 0)],
                        ["Trade (Counts)", "Win", cfmt(trade.win_count || 0)],
                        ["Trade (Counts)", "Loss", cfmt(trade.loss_count || 0)],

                        ["Trade (Amounts)", "Total Amount", nfmt2(trade.total_amount || 0)],
                        ["Trade (Amounts)", "Pending Amount", nfmt2(trade.pending_amount || 0)],
                        ["Trade (Amounts)", "Win Amount", nfmt2(trade.win_amount || 0)],
                        ["Trade (Amounts)", "Loss Amount", nfmt2(trade.loss_amount || 0)],
                    ],
                    styles: { fontSize: 9, cellPadding: 5 },
                    headStyles: { fillColor: [32, 201, 151], textColor: 0 },
                    columnStyles: { 2: { halign: "right" } },
                    margin: { left: marginX, right: marginX },
                });

                y = doc.lastAutoTable.finalY + 16;
            });

            // Salary record is NOT added to PDF

            // Filename updated exactly as requested
            const filename = `mobile_${fromDate}_${toDate}.pdf`;
            doc.save(filename);

            toast.success("PDF downloaded");
        } catch (e) {
            console.log(e);
            toast.error("Failed to generate PDF");
        } finally {
            setDownloading(false);
        }
    }, [validateDates, report, fromDate, toDate, levels, totals, mobileDisplay, selfDeposit, selfWithdrawal, selfNetFlow]);

    // Input change handlers: clear previous data so user must click Fetch to load new data
    const onChangeFrom = (v) => {
        setFromDate(v);
        clearLoadedData();
    };
    const onChangeTo = (v) => {
        setToDate(v);
        clearLoadedData();
    };

    return (
        <PageTransition>
            <style>{`
        .hero {
          border-radius: 14px;
          background: linear-gradient(135deg, rgba(13,110,253,.10), rgba(32,201,151,.08));
          border: 1px solid rgba(15,23,42,.08);
          box-shadow: 0 10px 34px rgba(15,23,42,.08);
        }
        .profile-card {
          border-radius: 14px;
          overflow: hidden;
          box-shadow: 0 10px 34px rgba(15,23,42,.10);
          border: 1px solid rgba(15,23,42,.08);
        }
        .profile-avatar {
          width: 72px;
          height: 72px;
          border-radius: 18px;
          display:flex;
          align-items:center;
          justify-content:center;
          margin: 0 auto;
          background: rgba(13,110,253,.12);
          color: #0d6efd;
        }
        .profile-top {
          padding: 16px;
          background: rgba(255,255,255,.75);
        }
        .profile-meta {
          border-top: 1px dashed rgba(15,23,42,.12);
          padding-top: 14px;
          margin-top: 14px;
        }
        .filters-card {
          border-radius: 14px;
          border: 1px solid rgba(15,23,42,.08);
          box-shadow: 0 10px 34px rgba(15,23,42,.08);
        }

        .totals-wrap{
          display:grid;
          grid-template-columns: repeat(12, 1fr);
          gap: 12px;
        }
        .totals-card{
          grid-column: span 3;
          border-radius: 14px;
          border: 1px solid rgba(15,23,42,.08);
          box-shadow: 0 10px 34px rgba(15,23,42,.08);
          background: #fff;
          overflow:hidden;
        }
        @media (max-width: 992px){
          .totals-card{ grid-column: span 6; }
        }
        @media (max-width: 576px){
          .totals-card{ grid-column: span 12; }
        }

        .stat-pill{
          display:flex;
          gap: 12px;
          padding: 14px;
          align-items:flex-start;
        }
        .stat-pill-icon{
          width: 44px;
          height: 44px;
          border-radius: 14px;
          display:flex;
          align-items:center;
          justify-content:center;
          background: rgba(13,110,253,.10);
          color:#0d6efd;
          flex: 0 0 auto;
          border: 1px solid rgba(13,110,253,.15);
        }
        .stat-pill-label{
          font-size: 12px;
          color: rgba(15,23,42,.65);
          font-weight: 700;
          letter-spacing: .2px;
        }
        .stat-pill-value{
          margin-top: 4px;
          font-size: 18px;
          font-weight: 900;
          color: rgba(15,23,42,.92);
          line-height: 1.1;
        }

        .level-wrap{
          border-radius: 14px;
          border: 1px solid rgba(15,23,42,.08);
          box-shadow: 0 10px 34px rgba(15,23,42,.08);
          background:#fff;
          overflow:hidden;
          margin-bottom: 14px;
        }
        .level-summary{
          list-style:none;
          cursor:pointer;
          padding: 14px 14px;
          display:flex;
          align-items:flex-start;
          justify-content: space-between;
          gap: 12px;
          background: linear-gradient(135deg, rgba(15,23,42,.03), rgba(13,110,253,.04));
          border-bottom: 1px solid rgba(15,23,42,.08);
        }
        .level-summary::-webkit-details-marker{ display:none; }
        .level-badge{
          display:inline-flex;
          gap: 8px;
          align-items:center;
          padding: 8px 12px;
          border-radius: 999px;
          background: rgba(13,110,253,.12);
          color:#0d6efd;
          font-weight: 900;
          border: 1px solid rgba(13,110,253,.18);
          white-space: nowrap;
        }
        .level-sub{
          margin-top: 8px;
          color: rgba(15,23,42,.65);
          font-size: 12px;
          font-weight: 700;
        }
        .level-summary-right{
          display:flex;
          gap: 8px;
          flex-wrap: wrap;
          justify-content: flex-end;
        }
        .pill{
          display:inline-flex;
          align-items:center;
          gap: 8px;
          padding: 7px 10px;
          border-radius: 999px;
          font-size: 12px;
          font-weight: 800;
          border: 1px solid rgba(15,23,42,.10);
          background: rgba(255,255,255,.70);
          color: rgba(15,23,42,.80);
          white-space: nowrap;
        }
        .pill-blue{
          border-color: rgba(13,110,253,.18);
          background: rgba(13,110,253,.08);
          color: rgba(13,110,253,.95);
        }
        .pill-slate{
          border-color: rgba(15,23,42,.12);
          background: rgba(15,23,42,.04);
          color: rgba(15,23,42,.85);
        }
        .level-body{ padding: 14px; }

        .mini-card{
          border-radius: 14px;
          border: 1px solid rgba(15,23,42,.08);
          background:#fff;
          overflow:hidden;
        }
        .mini-card-head{
          padding: 12px 12px 10px 12px;
          border-bottom: 1px dashed rgba(15,23,42,.12);
          background: rgba(15,23,42,.02);
        }
        .mini-card-title{
          display:flex;
          align-items:center;
          gap: 10px;
          font-weight: 900;
          color: rgba(15,23,42,.90);
        }
        .mini-card-ic{
          width: 34px; height: 34px;
          border-radius: 12px;
          display:flex; align-items:center; justify-content:center;
          border: 1px solid rgba(15,23,42,.10);
          background: #fff;
        }
        .mini-card-body{ padding: 12px; }

        .tone-green .mini-card-ic{ background: rgba(25,135,84,.10); color:#198754; border-color: rgba(25,135,84,.20); }
        .tone-blue .mini-card-ic{ background: rgba(13,110,253,.10); color:#0d6efd; border-color: rgba(13,110,253,.20); }
        .tone-purple .mini-card-ic{ background: rgba(111,66,193,.10); color:#6f42c1; border-color: rgba(111,66,193,.20); }
        .tone-amber .mini-card-ic{ background: rgba(255,193,7,.16); color:#b58100; border-color: rgba(255,193,7,.35); }

        .kv-row{
          display:flex;
          justify-content: space-between;
          gap: 10px;
          padding: 8px 0;
          border-top: 1px dashed rgba(15,23,42,.10);
        }
        .kv-row:first-child{ border-top: 0; padding-top: 0; }
        .kv-k{ font-size: 12px; color: rgba(15,23,42,.65); font-weight: 800; }
        .kv-v{ font-size: 13px; color: rgba(15,23,42,.92); font-weight: 900; }
        .kv-divider{ height: 1px; background: rgba(15,23,42,.08); margin: 8px 0; }

        .trade-grid{
          display:grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
        }
        @media (max-width: 576px){
          .trade-grid{ grid-template-columns: 1fr; }
        }
        .trade-col{
          border: 1px solid rgba(15,23,42,.08);
          border-radius: 12px;
          padding: 12px;
          background: rgba(15,23,42,.02);
        }
        .trade-h{
          font-weight: 900;
          color: rgba(15,23,42,.85);
          margin-bottom: 8px;
          display:flex;
          align-items:center;
          gap: 8px;
        }

        .section-head{
          display:flex;
          align-items:center;
          justify-content: space-between;
          gap: 12px;
          margin-bottom: 10px;
        }
        .section-title{
          font-weight: 900;
          color: rgba(15,23,42,.92);
          display:flex;
          align-items:center;
          gap: 10px;
        }
      `}</style>

            {/* Header */}
            <div className="d-flex flex-wrap align-items-center justify-content-between mb-3">
                <div>
                    <div className="d-flex align-items-center gap-3 mb-1">
                        <button
                            type="button"
                            className="btn btn-outline-secondary d-inline-flex align-items-center gap-2"
                            onClick={() => navigate(-1)}
                            title="Back"
                            style={{ padding: "0.5rem 1rem" }}
                        >
                            <FaArrowLeft /> Back
                        </button>
                        <h2 className="fw-bold mb-0">Leader Report</h2>
                    </div>
                    <div className="text-muted small">Updated: {lastUpdatedText}</div>
                </div>

                <div className="d-flex gap-2 flex-wrap">
                    <button
                        type="button"
                        className="btn btn-outline-primary d-inline-flex align-items-center gap-2"
                        onClick={() => navigate(`/user-downline-report/${encodeURIComponent(username)}`)}
                        title="Downline statistics"
                        style={{ padding: "0.5rem 1rem" }}
                    >
                        <FaChartBar /> Statistics
                    </button>

                    <button
                        type="button"
                        className="btn btn-outline-danger d-inline-flex align-items-center gap-2"
                        onClick={downloadPdf}
                        disabled={downloading || !report?.profile}
                        title="Download PDF"
                        style={{ padding: "0.5rem 1rem" }}
                    >
                        <FaFilePdf />
                        {downloading ? "Generating..." : "Download PDF"}
                    </button>

                    <button
                        type="button"
                        className="btn btn-primary d-inline-flex align-items-center gap-2"
                        onClick={fetchReport}
                        disabled={loading}
                        title="Refresh"
                        style={{ padding: "0.5rem 1rem" }}
                    >
                        <FaSyncAlt className={loading ? "tb-spin" : ""} />
                        {loading ? "Refreshing..." : "Refresh"}
                    </button>
                </div>
            </div>

            {/* Filters */}
            <div className="card filters-card mb-4">
                <div className="card-body">
                    <form onSubmit={onSubmit}>
                        <div className="row g-3 align-items-end">
                            <div className="col-12 col-md-4">
                                <label className="form-label small text-muted mb-1">
                                    <FaCalendarAlt className="me-2" />
                                    From Date
                                </label>
                                <input
                                    type="date"
                                    className="form-control"
                                    value={fromDate}
                                    onChange={(e) => onChangeFrom(e.target.value)}
                                    required
                                />
                            </div>

                            <div className="col-12 col-md-4">
                                <label className="form-label small text-muted mb-1">
                                    <FaCalendarAlt className="me-2" />
                                    To Date
                                </label>
                                <input
                                    type="date"
                                    className="form-control"
                                    value={toDate}
                                    onChange={(e) => onChangeTo(e.target.value)}
                                    required
                                />
                            </div>

                            <div className="col-12 col-md-4 d-flex gap-2">
                                <button
                                    type="submit"
                                    className="btn btn-primary w-100 d-inline-flex align-items-center justify-content-center gap-2"
                                    disabled={loading}
                                >
                                    <FaChartBar />
                                    {loading ? "Loading..." : "Fetch Report"}
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>

            {/* Profile Top Card */}
            <div className="card profile-card mb-4 hero">
                <div className="card-body profile-top">
                    {!profile ? (
                        <div className="text-center text-muted py-4">
                            <div className="profile-avatar mb-3">
                                <FaUserCircle size={34} />
                            </div>
                            <div className="fw-bold">No profile data available</div>
                        </div>
                    ) : (
                        <div className="row g-3 align-items-center">
                            <div className="col-12 col-md-3 text-center">
                                <div className="profile-avatar mb-2">
                                    <FaUserCircle size={34} />
                                </div>
                                <div className="fw-bold fs-5">{safeText(profile?.name)}</div>
                                <div className="mt-2">
                                    {profile?.status ? (
                                        <span className="badge bg-success px-3 py-2">
                                            <FaCheckCircle className="me-2" /> Active
                                        </span>
                                    ) : (
                                        <span className="badge bg-danger px-3 py-2">
                                            <FaTimesCircle className="me-2" /> Inactive
                                        </span>
                                    )}
                                </div>
                            </div>

                            <div className="col-12 col-md-9">
                                <div className="row g-3">
                                    <div className="col-12 col-md-6">
                                        <div className="d-flex align-items-center gap-2 text-muted small">
                                            <FaPhoneAlt className="text-primary" /> Mobile
                                        </div>
                                        <div className="fw-semibold">{mobileDisplay}</div>
                                    </div>
                                    <div className="col-12 col-md-6">
                                        <div className="d-flex align-items-center gap-2 text-muted small">
                                            <FaEnvelope className="text-primary" /> Email
                                        </div>
                                        <div className="fw-semibold" style={{ wordBreak: "break-word" }}>
                                            {safeText(profile?.email)}
                                        </div>
                                    </div>

                                    <div className="col-12">
                                        <div className="profile-meta row g-3">
                                            <div className="col-12 col-md-4">
                                                <div className="text-muted small d-flex align-items-center gap-2">
                                                    <FaWallet className="text-primary" /> Total Balance
                                                </div>
                                                <div className="fw-semibold">{nfmt2(profile?.total_balance)}</div>
                                            </div>
                                            <div className="col-12 col-md-4">
                                                <div className="text-muted small d-flex align-items-center gap-2">
                                                    <FaWallet className="text-primary" /> Locked Balance
                                                </div>
                                                <div className="fw-semibold">{nfmt2(profile?.lock_balance)}</div>
                                            </div>
                                            <div className="col-12 col-md-4">
                                                <div className="text-muted small d-flex align-items-center gap-2">
                                                    <FaCoins className="text-primary" /> USDT
                                                </div>
                                                <div className="fw-semibold">{nfmt2(profile?.usdt)}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Self Transaction Summary */}
            <div
                className="card border-0 shadow-sm mb-4"
                style={{ borderRadius: 14, overflow: "hidden", border: "1px solid rgba(15,23,42,.08)" }}
            >
                <div className="card-header bg-white border-0 py-3">
                    <div className="section-head">
                        <div className="section-title">
                            <FaUserCircle className="text-primary" />
                            Self Transaction Summary
                        </div>
                        <span className="badge text-bg-primary">{nfmt2(selfNetFlow)}</span>
                    </div>
                </div>

                <div className="card-body pt-0">
                    <div className="row g-3">
                        <div className="col-12 col-md-4">
                            <div className="text-center p-3 border rounded-3">
                                <div className="text-muted small mb-1">Self Deposit</div>
                                <div className="h4 fw-bold text-success">{nfmt2(selfDeposit)}</div>
                            </div>
                        </div>
                        <div className="col-12 col-md-4">
                            <div className="text-center p-3 border rounded-3">
                                <div className="text-muted small mb-1">Self Withdrawal</div>
                                <div className="h4 fw-bold text-danger">{nfmt2(selfWithdrawal)}</div>
                            </div>
                        </div>
                        <div className="col-12 col-md-4">
                            <div className="text-center p-3 border rounded-3">
                                <div className="text-muted small mb-1">Self Net Flow</div>
                                <div className={`h4 fw-bold ${selfNetFlow >= 0 ? 'text-success' : 'text-danger'}`}>
                                    {selfNetFlow >= 0 ? '+' : ''}{nfmt2(selfNetFlow)}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Current Balances (not date range dependent) - MOVED TO TOP */}
            <div className="totals-wrap mb-4">
                <div className="totals-card">
                    <StatPill icon={<FaWallet />} label="Total Coin" value={nfmt2(totals.balanceTotals.coin)} />
                </div>
                <div className="totals-card">
                    <StatPill icon={<FaWallet />} label="Total Locked Coin" value={nfmt2(totals.balanceTotals.lock)} />
                </div>
                <div className="totals-card">
                    <StatPill icon={<FaCoins />} label="Total USDT" value={nfmt2(totals.balanceTotals.usdt)} />
                </div>
            </div>



            {/* Team Transaction Summary (date range dependent) */}
            <div className="totals-wrap mb-4">
                <div className="totals-card">
                    <StatPill icon={<FaMoneyBillWave />} label="Total Deposit" value={nfmt2(totals.depositTotal)} />
                </div>
                <div className="totals-card">
                    <StatPill icon={<FaExchangeAlt />} label="Total Withdrawal" value={nfmt2(totals.withdrawalTotal)} />
                </div>
                <div className="totals-card">
                    <StatPill icon={<FaChartBar />} label="Net Flow" value={nfmt2(totals.netFlow)} />
                </div>
                <div className="totals-card">
                    <StatPill icon={<FaChartBar />} label="Trade Total Amount" value={nfmt2(totals.tradeTotals.total_amount)} />
                </div>

                {/* Replaced Trade Counts card with Total Commission */}
                <div className="totals-card">
                    <StatPill icon={<FaCoins />} label="Total Commission" value={nfmt2(totals.commissionTotals.total)} />
                </div>
            </div>

            {/* Commission Totals Table */}
            <div
                className="card border-0 shadow-sm mb-4"
                style={{ borderRadius: 14, overflow: "hidden", border: "1px solid rgba(15,23,42,.08)" }}
            >
                <div className="card-header bg-white border-0 py-3">
                    <div className="section-head">
                        <div className="section-title">
                            <FaCoins className="text-primary" />
                            Commission Totals
                        </div>
                        <span className="badge text-bg-secondary">{nfmt2(totals.commissionTotals.total)}</span>
                    </div>
                </div>

                <div className="card-body pt-0">
                    <div className="table-responsive">
                        <table className="table table-sm align-middle mb-0">
                            <thead>
                                <tr className="text-muted">
                                    <th>Type</th>
                                    <th className="text-end">Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td className="fw-semibold">Refer</td>
                                    <td className="text-end fw-bold">{nfmt2(totals.commissionTotals.refer)}</td>
                                </tr>
                                <tr>
                                    <td className="fw-semibold">Self Deposit</td>
                                    <td className="text-end fw-bold">{nfmt2(totals.commissionTotals.self_deposit)}</td>
                                </tr>
                                <tr>
                                    <td className="fw-semibold">Deposit</td>
                                    <td className="text-end fw-bold">{nfmt2(totals.commissionTotals.deposit)}</td>
                                </tr>
                                <tr>
                                    <td className="fw-semibold">Trade</td>
                                    <td className="text-end fw-bold">{nfmt2(totals.commissionTotals.trade)}</td>
                                </tr>
                                <tr>
                                    <td className="fw-bold">Total</td>
                                    <td className="text-end fw-bold">{nfmt2(totals.commissionTotals.total)}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Level-wise sections (ALL expanded by default) */}
            <div className="mb-4">
                <div className="section-head">
                    <div className="section-title">
                        <FaLayerGroup className="text-primary" />
                        Level-wise Breakdown
                    </div>
                    <span className="badge text-bg-secondary">{levels.length}</span>
                </div>

                {levels.map((n) => {
                    const s = report?.stats || {};
                    const dep = s?.deposit?.[`level_${n}`] ?? 0;
                    const wd = s?.withdrawal?.[`level_${n}`] ?? 0;

                    const trade = s?.trade?.[`level_${n}`] || {};
                    const balance = s?.balance?.[`level_${n}`] || {};
                    const commission = s?.commission?.[`leve_${n}`] || {}; // API typo

                    return (
                        <LevelSection
                            key={n}
                            level={n}
                            deposit={dep}
                            withdrawal={wd}
                            trade={trade}
                            balance={balance}
                            commission={commission}
                            defaultOpen={true}
                        />
                    );
                })}
            </div>

            {/* Salary Record (Page only) */}
            <div
                className="card border-0 shadow-sm mb-4"
                style={{ borderRadius: 14, overflow: "hidden", border: "1px solid rgba(15,23,42,.08)" }}
            >
                <div className="card-header bg-white border-0 py-3">
                    <div className="section-head">
                        <div className="section-title">
                            <FaUsers className="text-primary" />
                            Salary Record
                        </div>
                        <span className="badge text-bg-secondary">{cfmt(report?.salary_record?.length || 0)}</span>
                    </div>
                </div>

                <div className="card-body pt-0">
                    {Array.isArray(report?.salary_record) && report.salary_record.length > 0 ? (
                        <div className="table-responsive">
                            <table className="table table-sm align-middle mb-0">
                                <thead>
                                    <tr className="text-muted">
                                        {Object.keys(report.salary_record[0] || {})
                                            .filter((k) => k !== "salary_id")
                                            .map((k) => (
                                                <th key={k}>{k === "create_date" ? "Create Date" : k}</th>
                                            ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {report.salary_record.map((row, idx) => {
                                        const cols = Object.keys(report.salary_record[0] || {}).filter((k) => k !== "salary_id");
                                        return (
                                            <tr key={idx}>
                                                {cols.map((k) => (
                                                    <td key={k} className="fw-semibold">
                                                        {k === "create_date"
                                                            ? row?.[k]
                                                                ? new Date(row[k]).toLocaleString("en-IN", { hour12: true })
                                                                : "—"
                                                            : safeText(row?.[k])}
                                                    </td>
                                                ))}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="text-muted py-3">No salary records found.</div>
                    )}
                </div>
            </div>
        </PageTransition>
    );
}